import { Column } from '@tanstack/react-table';
import i18next from 'i18next';

import { DueDateClassified } from '@work4all/models/lib/Enums/DueDateClassified.enum';
import { InvoiceForm } from '@work4all/models/lib/Enums/InvoiceForm.enum';
import { PaymentStatus } from '@work4all/models/lib/Enums/PaymentStatus.enum';
import { RaAccountingStatus } from '@work4all/models/lib/Enums/RaAccountingStatus.enum';
import { ReAccountingStatus } from '@work4all/models/lib/Enums/ReAccountingStatus.enum';
import { REBelegart } from '@work4all/models/lib/Enums/REBelegart.enum';
import { RePermitStatus } from '@work4all/models/lib/Enums/RePermitStatus.enum';
import { SalesOpportunityStatus } from '@work4all/models/lib/Enums/SalesOpportunityStatus.enum';
import { ServiceContractRythm } from '@work4all/models/lib/Enums/ServiceContractRythm.enum';
import { TrafficLightStatus } from '@work4all/models/lib/Enums/TrafficLightStatus.enum';
import { VacationRequestStatus } from '@work4all/models/lib/Enums/VacationRequestStatus.enum';

import { assertNever } from '@work4all/utils';

import { EmailKindChangeLookUp } from '../../../components/entity-picker/email-kind-picker/EmailKindPicker';
import { LedgerAccountKind } from '../../../components/entity-picker/ledger-account-kind-picker/LedgerAccountKind.enum';
import { TaskStatusChangeLookUp } from '../../../components/entity-picker/task-status-picker/TaskStatusPicker';
import { TicketStatusChangeLookUp } from '../../../components/entity-picker/ticket-status-picker/TicketStatusPicker';
import { FilterType } from '../types';

import { dateToString, numbersToString } from './filters/filterConversion';

const tryGenerateDetailedPickerTitle = (header, value): string | null => {
  if (value?.length !== 1) {
    return null;
  }

  const [first] = value;

  if (typeof first === 'string') return first;

  const nameLikeProperties = ['displayName', 'name', 'note'];

  for (const property of nameLikeProperties) {
    if (property in first) {
      return `${header}: ${first[property]}`;
    }
  }

  return null;
};

const generateGenericPickerTitle = (header, value): string => {
  return i18next.t('FILTERS.GENERIC_PICKER_TITLE', {
    header,
    number: value?.length || 1,
  });
};

export const genFilterTitleV8 = (col: Column<unknown>) => {
  let val: string;

  const filterType = col.columnDef.filterType;
  const filterValue = (col.getFilterValue() as any).value;
  switch (filterType) {
    case FilterType.ClosedStatus:
      return i18next.t(col.columnDef.filterParams.closedTextKey);
    case FilterType.Search:
      val = filterValue as string;
      break;
    case FilterType.TravelReceiptStatus:
      val = i18next.t(
        'COMMON.TRAVELRECEIPTS_STATUS_' + filterValue?.toUpperCase()
      );
      break;
    case FilterType.TravelExpensesOpenStatus:
      val = i18next.t(
        'COMMON.TRAVELEXPENSES_OPEN_STATUS_' + filterValue?.toUpperCase()
      );
      break;
    case FilterType.Date: {
      const start = filterValue?.startDate;
      const end = filterValue?.endDate;
      val = dateToString(start, end);

      break;
    }
    case FilterType.Number:
      if (filterValue.from != null || filterValue.to != null)
        val = numbersToString(filterValue.from, filterValue.to);
      break;
    case FilterType.TaskStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(TaskStatusChangeLookUp)
      );

    case FilterType.TicketStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(TicketStatusChangeLookUp)
      );

    case FilterType.PaymentStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(PaymentStatus)
      );
    case FilterType.InvoiceForm:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(InvoiceForm)
      );
    case FilterType.ReAccountingStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(ReAccountingStatus)
      );
    case FilterType.RaAccountingStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(RaAccountingStatus)
      );
    case FilterType.RythmStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(ServiceContractRythm)
      );
    case FilterType.PermitStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(RePermitStatus)
      );
    case FilterType.VacationStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(VacationRequestStatus)
      );
    case FilterType.InvoiceKind:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(REBelegart)
      );
    case FilterType.LedgerAccountKind:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(LedgerAccountKind)
      );
    case FilterType.DueDateClassified:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(DueDateClassified)
      );
    case FilterType.SalesOpportunitiesStatusPicker:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(SalesOpportunityStatus)
      );
    case FilterType.EmailKind: {
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(EmailKindChangeLookUp)
      );
    }
    case FilterType.AppointmentBillable: {
      return (
        tryGenerateDetailedPickerTitle(
          i18next.t('APPOINTMENT.BILLING_STATUS'),
          filterValue
        ) ??
        generateGenericPickerTitle(
          i18next.t('APPOINTMENT.BILLING_STATUS'),
          filterValue
        )
      );
    }
    case FilterType.RequiredTime: {
      const from = filterValue?.from ?? null;
      const to = filterValue?.to ?? null;

      if (from != null || to != null) {
        const multiplier =
          col.columnDef.cellParams?.convertTo === 'hours' ? 8 : 1;

        val = numbersToString(
          from != null ? from * multiplier : null,
          to != null ? to * multiplier : null
        );
      }
      break;
    }
    case FilterType.Primitive:
      if (filterValue.length === 1) {
        const value = filterValue[0] || i18next.t('COMMON.NONE');
        return `${col.columnDef.header}: ${value}`;
      }
      return generateGenericPickerTitle(col.columnDef.header, filterValue);
    case FilterType.SalesOpportunitiesGroupPicker:
    case FilterType.VacationKind:
    case FilterType.EInvoiceFormat:
    case FilterType.Boolean:
    case FilterType.BooleanNumber:
    case FilterType.ChronoContactPicker:
    case FilterType.ObjectType:
    case FilterType.ArticleKind:
    case FilterType.TaskPriority:
    case FilterType.Enum:
    case FilterType.TicketPriority:
    case FilterType.AppointmentState:
    case FilterType.Picker: {
      return (
        tryGenerateDetailedPickerTitle(col.columnDef.header, filterValue) ??
        generateGenericPickerTitle(col.columnDef.header, filterValue)
      );
    }
    case FilterType.Check:
      return `${col.columnDef.header}: ${i18next.t(
        'FILTERS.CHECKED_ITEMS_ONLY'
      )}`;
    case FilterType.ProjectTrafficLightStatus:
      return generateFixedPickerTitle(
        filterValue,
        col,
        Object.entries(TrafficLightStatus)
      );
    default:
      assertNever(filterType, `Unknown filter type ${filterType}`);
  }

  return `${col.columnDef.header as string}: ${val}`;
};

function generateFixedPickerTitle(
  filterValue: any,
  col: Column<unknown>,
  //eslint-disable-next-line
  entries: [string, any][]
) {
  const values = filterValue
    ?.map((original) => {
      const distinctBy = entries.find((y) =>
        Array.isArray(y[1]) ? y[1].includes(original.id) : y[1] === original.id
      )?.[0];

      if (original.id === distinctBy) return original;
      return null;
    })
    .filter((x) => x);

  return (
    tryGenerateDetailedPickerTitle(col.columnDef.header, values) ??
    generateGenericPickerTitle(col.columnDef.header, values)
  );
}
